/**
 * JWT Token Inspector & Debugger
 * JustOnlyOneThing Developer Utilities
 * 100% Client-Side • Web Crypto API HMAC Verification • Zero Server Uploads
 */

(function () {
  'use strict';

  // --- UTF-8 SAFE BASE64URL HELPERS ---
  function base64UrlEncode(str) {
    const utf8Bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < utf8Bytes.length; i++) {
      binary += String.fromCharCode(utf8Bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  function base64UrlDecode(b64UrlStr) {
    if (!b64UrlStr) return '';
    let base64 = b64UrlStr.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder().decode(bytes);
    } catch (e) {
      return '';
    }
  }

  function arrayBufferToBase64Url(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  // --- PRESET TOKENS ---
  const now = Math.floor(Date.now() / 1000);

  const PRESETS = {
    session: {
      name: 'User Session (HS256)',
      header: { alg: 'HS256', typ: 'JWT' },
      payload: {
        sub: 'usr_99812401',
        name: 'John Doe',
        email: 'john.doe@example.com',
        role: 'developer',
        iat: now - 3600,
        exp: now + 7200
      },
      secret: 'your-256-bit-secret',
      isSecretB64: false
    },
    oauth: {
      name: 'OAuth2 Access Token',
      header: { alg: 'HS256', typ: 'at+jwt', kid: 'auth-key-01' },
      payload: {
        iss: 'https://auth.example.com/',
        sub: 'usr_oauth_4021',
        aud: ['https://api.example.com/v1', 'https://api.example.com/v2'],
        client_id: 'webapp_client_prod',
        scope: 'read:profile write:reports openid email',
        iat: now - 600,
        exp: now + 3600
      },
      secret: 'oauth-service-secret-token-key',
      isSecretB64: false
    },
    admin: {
      name: 'Admin RBAC Claims',
      header: { alg: 'HS256', typ: 'JWT' },
      payload: {
        sub: 'admin_5501',
        name: 'Super Admin',
        org: 'Security Operations',
        permissions: [
          'system:admin',
          'users:read',
          'users:write',
          'billing:manage',
          'audit:export'
        ],
        iat: now - 1800,
        exp: now + 86400
      },
      secret: 'top-secret-admin-signature-key',
      isSecretB64: false
    },
    expired: {
      name: 'Expired Token Demo',
      header: { alg: 'HS256', typ: 'JWT' },
      payload: {
        sub: 'expired_user_102',
        name: 'Alice Smith',
        email: 'alice.smith@example.org',
        iat: 1609459200, // Jan 1, 2021
        exp: 1609462800  // Expired 1 hour later
      },
      secret: 'your-256-bit-secret',
      isSecretB64: false
    },
    hs512: {
      name: 'HMAC-SHA512 Token',
      header: { alg: 'HS512', typ: 'JWT' },
      payload: {
        sub: 'fin_service_01',
        name: 'Automated Payments Engine',
        scope: 'transfers:execute accounts:audit',
        iat: now - 300,
        exp: now + 3600
      },
      secret: 'super-long-512-bit-high-entropy-secret-key-for-payments-encryption',
      isSecretB64: false
    }
  };

  // --- STATE ---
  const state = {
    token: '',
    headerObj: { ...PRESETS.session.header },
    payloadObj: { ...PRESETS.session.payload },
    signatureB64: '',
    secret: PRESETS.session.secret,
    isSecretB64: false,
    isValidStructure: true,
    isSignatureVerified: false,
    activeCodeTab: 'js',
    isSyncing: false
  };

  // --- CRYPTOGRAPHIC SIGNING & VERIFICATION (WEB CRYPTO API) ---
  async function computeHmacSignature(headerB64, payloadB64, secretStr, alg, isB64) {
    if (alg === 'none') return '';
    if (!window.crypto || !window.crypto.subtle) return '';

    let hashName = 'SHA-256';
    if (alg === 'HS384') hashName = 'SHA-384';
    if (alg === 'HS512') hashName = 'SHA-512';

    let keyBytes;
    if (isB64) {
      try {
        let b64 = secretStr.replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';
        const bin = atob(b64);
        keyBytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) keyBytes[i] = bin.charCodeAt(i);
      } catch (e) {
        keyBytes = new TextEncoder().encode(secretStr);
      }
    } else {
      keyBytes = new TextEncoder().encode(secretStr);
    }

    try {
      const cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'HMAC', hash: { name: hashName } },
        false,
        ['sign']
      );

      const dataToSign = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
      const signatureBuffer = await window.crypto.subtle.sign('HMAC', cryptoKey, dataToSign);
      return arrayBufferToBase64Url(signatureBuffer);
    } catch (err) {
      console.warn('HMAC signing failed:', err);
      return '';
    }
  }

  // --- ENCODE FROM DECODED JSON ---
  async function encodeFromDecoded() {
    if (state.isSyncing) return;
    state.isSyncing = true;

    try {
      const headerStr = JSON.stringify(state.headerObj, null, 2);
      const payloadStr = JSON.stringify(state.payloadObj, null, 2);

      const headerB64 = base64UrlEncode(JSON.stringify(state.headerObj));
      const payloadB64 = base64UrlEncode(JSON.stringify(state.payloadObj));

      const alg = state.headerObj.alg || 'HS256';
      let sigB64 = '';

      if (alg.startsWith('HS')) {
        sigB64 = await computeHmacSignature(headerB64, payloadB64, state.secret, alg, state.isSecretB64);
        state.isSignatureVerified = true;
      } else if (alg === 'none') {
        sigB64 = '';
        state.isSignatureVerified = true;
      } else {
        // RS256 / ES256 fallback simulation or preserve
        sigB64 = state.signatureB64 || 'signature_preview';
        state.isSignatureVerified = false;
      }

      state.signatureB64 = sigB64;
      state.token = `${headerB64}.${payloadB64}.${sigB64}`;
      state.isValidStructure = true;

      renderEncodedViewer(headerB64, payloadB64, sigB64);
      updateStatusBadges();
      updateClaimsTable();
      updateTimeAuditor();
      updateCodeSnippets();
    } finally {
      state.isSyncing = false;
    }
  }

  // --- DECODE FROM ENCODED TOKEN ---
  async function decodeFromToken(tokenString) {
    if (state.isSyncing) return;
    state.isSyncing = true;

    state.token = tokenString.trim();

    try {
      const parts = state.token.split('.');

      if (parts.length < 2 || parts.length > 3) {
        state.isValidStructure = false;
        state.isSignatureVerified = false;
        renderInvalidToken();
        updateStatusBadges();
        return;
      }

      const [headerB64, payloadB64, sigB64 = ''] = parts;
      state.signatureB64 = sigB64;

      const headerJsonStr = base64UrlDecode(headerB64);
      const payloadJsonStr = base64UrlDecode(payloadB64);

      let headerParsed = {};
      let payloadParsed = {};

      try {
        headerParsed = JSON.parse(headerJsonStr);
      } catch (e) {
        state.isValidStructure = false;
        renderInvalidToken('Invalid Header JSON');
        return;
      }

      try {
        payloadParsed = JSON.parse(payloadJsonStr);
      } catch (e) {
        state.isValidStructure = false;
        renderInvalidToken('Invalid Payload JSON');
        return;
      }

      state.headerObj = headerParsed;
      state.payloadObj = payloadParsed;
      state.isValidStructure = true;

      // Update editors
      const headerEditor = document.getElementById('jwtHeaderEditor');
      const payloadEditor = document.getElementById('jwtPayloadEditor');
      if (headerEditor) headerEditor.value = JSON.stringify(state.headerObj, null, 2);
      if (payloadEditor) payloadEditor.value = JSON.stringify(state.payloadObj, null, 2);

      // Verify signature
      const alg = state.headerObj.alg || 'HS256';
      if (alg.startsWith('HS')) {
        const expectedSig = await computeHmacSignature(headerB64, payloadB64, state.secret, alg, state.isSecretB64);
        state.isSignatureVerified = expectedSig.length > 0 && expectedSig === sigB64;
      } else if (alg === 'none') {
        state.isSignatureVerified = sigB64 === '';
      } else {
        state.isSignatureVerified = false;
      }

      renderEncodedViewer(headerB64, payloadB64, sigB64);
      updateStatusBadges();
      updateClaimsTable();
      updateTimeAuditor();
      updateCodeSnippets();
    } finally {
      state.isSyncing = false;
    }
  }

  // --- RENDER THREE-COLOR ENCODED VIEWER ---
  function renderEncodedViewer(h, p, s) {
    const display = document.getElementById('jwtTokenDisplay');
    const textarea = document.getElementById('jwtTokenTextarea');
    if (!display) return;

    if (!state.isValidStructure) {
      display.innerHTML = `<span style="color:#ef4444;">${escapeHtml(state.token)}</span>`;
      return;
    }

    display.innerHTML = `
      <span class="jwt-part-header" title="Decoded Header (Algorithm & Token Type)">${escapeHtml(h)}</span><span class="jwt-part-dot">.</span><span class="jwt-part-payload" title="Decoded Payload (Claims & User Data)">${escapeHtml(p)}</span><span class="jwt-part-dot">.</span><span class="jwt-part-sig" title="Signature">${escapeHtml(s)}</span>
    `;

    if (textarea && textarea.value !== state.token) {
      textarea.value = state.token;
    }
  }

  function renderInvalidToken(msg = 'Invalid JWT Format') {
    const display = document.getElementById('jwtTokenDisplay');
    if (display) {
      display.innerHTML = `<span style="color:#ef4444;">⚠️ ${escapeHtml(msg)}: Expected 3 base64url segments separated by dots.</span>`;
    }
  }

  // --- UPDATE STATUS BADGES ---
  function updateStatusBadges() {
    const structBadge = document.getElementById('jwtStructBadge');
    const sigBadge = document.getElementById('jwtSigBadge');

    if (structBadge) {
      if (state.isValidStructure) {
        structBadge.className = 'jwt-badge valid';
        structBadge.innerHTML = '<span>✓</span> Valid JWT Structure';
      } else {
        structBadge.className = 'jwt-badge invalid';
        structBadge.innerHTML = '<span>✕</span> Invalid Structure';
      }
    }

    if (sigBadge) {
      const alg = state.headerObj.alg || 'HS256';
      if (!state.isValidStructure) {
        sigBadge.className = 'jwt-badge invalid';
        sigBadge.innerHTML = '<span>✕</span> Token Malformed';
      } else if (alg === 'none') {
        sigBadge.className = 'jwt-badge warning';
        sigBadge.innerHTML = '<span>⚠️</span> Algorithm: None (Unsigned)';
      } else if (state.isSignatureVerified) {
        sigBadge.className = 'jwt-badge valid';
        sigBadge.innerHTML = '<span>✓</span> Signature Verified';
      } else if (alg.startsWith('HS')) {
        sigBadge.className = 'jwt-badge invalid';
        sigBadge.innerHTML = '<span>✕</span> Invalid Signature';
      } else {
        sigBadge.className = 'jwt-badge warning';
        sigBadge.innerHTML = `<span>ℹ️</span> Asymmetric ${escapeHtml(alg)}`;
      }
    }
  }

  // --- TIMESTAMP AUDITOR ---
  function updateTimeAuditor() {
    const auditor = document.getElementById('jwtTimeAuditor');
    if (!auditor) return;

    const payload = state.payloadObj || {};
    const currentTime = Math.floor(Date.now() / 1000);

    let html = '';

    // Check exp (Expiration)
    if (typeof payload.exp === 'number') {
      const exp = payload.exp;
      const isExpired = exp < currentTime;
      const diffSec = Math.abs(exp - currentTime);
      const relTime = formatDuration(diffSec);

      const expDate = new Date(exp * 1000).toUTCString();
      const badgeClass = isExpired ? 'invalid' : 'valid';
      const badgeText = isExpired ? `⚠️ Expired ${relTime} ago` : `✓ Expires in ${relTime}`;

      html += `
        <div class="jwt-time-row">
          <div>
            <strong>exp (Expiration):</strong> <code style="color:#f472b6;">${exp}</code> 
            <span style="color:var(--text-muted); margin-left:0.35rem;">(${expDate})</span>
          </div>
          <div style="display:flex; align-items:center; gap:0.4rem;">
            <span class="jwt-badge ${badgeClass}" style="font-size:0.72rem; padding:0.15rem 0.45rem;">${badgeText}</span>
            <div class="jwt-time-actions">
              <button type="button" class="jwt-time-btn" data-add-sec="3600" title="Add 1 Hour">+1h</button>
              <button type="button" class="jwt-time-btn" data-add-sec="86400" title="Add 1 Day">+1d</button>
              <button type="button" class="jwt-time-btn" data-add-sec="604800" title="Add 7 Days">+7d</button>
            </div>
          </div>
        </div>
      `;
    }

    // Check iat (Issued At)
    if (typeof payload.iat === 'number') {
      const iat = payload.iat;
      const iatDate = new Date(iat * 1000).toUTCString();
      const diffSec = Math.abs(currentTime - iat);
      const relTime = formatDuration(diffSec);
      const timingLabel = iat <= currentTime ? `Issued ${relTime} ago` : `Issued ${relTime} in future`;

      html += `
        <div class="jwt-time-row" style="margin-top:0.35rem;">
          <div>
            <strong>iat (Issued At):</strong> <code style="color:#a855f7;">${iat}</code> 
            <span style="color:var(--text-muted); margin-left:0.35rem;">(${iatDate})</span>
          </div>
          <div style="display:flex; align-items:center; gap:0.4rem;">
            <span class="jwt-badge valid" style="font-size:0.72rem; padding:0.15rem 0.45rem;">${timingLabel}</span>
            <button type="button" class="jwt-time-btn" id="jwtBtnSetNow" title="Set iat to now and exp to +1h">Set Now</button>
          </div>
        </div>
      `;
    }

    // Check nbf (Not Before)
    if (typeof payload.nbf === 'number') {
      const nbf = payload.nbf;
      const nbfDate = new Date(nbf * 1000).toUTCString();
      const isValidNow = currentTime >= nbf;
      html += `
        <div class="jwt-time-row" style="margin-top:0.35rem;">
          <div>
            <strong>nbf (Not Before):</strong> <code style="color:#38bdf8;">${nbf}</code>
            <span style="color:var(--text-muted); margin-left:0.35rem;">(${nbfDate})</span>
          </div>
          <span class="jwt-badge ${isValidNow ? 'valid' : 'invalid'}" style="font-size:0.72rem; padding:0.15rem 0.45rem;">
            ${isValidNow ? '✓ Active' : '✕ Not Active Yet'}
          </span>
        </div>
      `;
    }

    if (!html) {
      html = '<span style="color:var(--text-muted); font-style:italic;">No standard timestamp claims (exp, iat, nbf) found in payload.</span>';
    }

    auditor.innerHTML = html;

    // Attach listeners on quick-add time buttons
    auditor.querySelectorAll('.jwt-time-btn[data-add-sec]').forEach(btn => {
      btn.addEventListener('click', () => {
        const addSec = parseInt(btn.getAttribute('data-add-sec'), 10);
        const curExp = typeof state.payloadObj.exp === 'number' ? state.payloadObj.exp : Math.floor(Date.now() / 1000);
        state.payloadObj.exp = curExp + addSec;
        const payloadEditor = document.getElementById('jwtPayloadEditor');
        if (payloadEditor) payloadEditor.value = JSON.stringify(state.payloadObj, null, 2);
        encodeFromDecoded();
      });
    });

    const btnSetNow = auditor.querySelector('#jwtBtnSetNow');
    if (btnSetNow) {
      btnSetNow.addEventListener('click', () => {
        const curNow = Math.floor(Date.now() / 1000);
        state.payloadObj.iat = curNow;
        state.payloadObj.exp = curNow + 3600;
        const payloadEditor = document.getElementById('jwtPayloadEditor');
        if (payloadEditor) payloadEditor.value = JSON.stringify(state.payloadObj, null, 2);
        encodeFromDecoded();
      });
    }
  }

  function formatDuration(sec) {
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const hrs = Math.floor(min / 60);
    const remMin = min % 60;
    if (hrs < 24) return remMin > 0 ? `${hrs}h ${remMin}m` : `${hrs}h`;
    const days = Math.floor(hrs / 24);
    const remHrs = hrs % 24;
    return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
  }

  // --- CLAIMS BREAKDOWN TABLE ---
  function updateClaimsTable() {
    const tbody = document.getElementById('jwtClaimsTableBody');
    if (!tbody) return;

    const payload = state.payloadObj || {};
    const keys = Object.keys(payload);

    if (keys.length === 0) {
      tbody.innerHTML = '<tr><td colspan="3" style="text-align:center; color:var(--text-muted); padding:1rem;">Payload is empty.</td></tr>';
      return;
    }

    const RFC_CLAIM_DESCRIPTIONS = {
      iss: 'Issuer — Identifies the principal that issued the JWT',
      sub: 'Subject — Identifies the principal that is the subject of the JWT',
      aud: 'Audience — Identifies the recipients that the JWT is intended for',
      exp: 'Expiration Time — Identifies the expiration time on or after which the JWT must not be accepted',
      nbf: 'Not Before — Identifies the time before which the JWT must not be accepted',
      iat: 'Issued At — Identifies the time at which the JWT was issued',
      jti: 'JWT ID — Provides a unique identifier for the JWT',
      client_id: 'OAuth2 Client ID — Client identifier issued by authorization server',
      scope: 'OAuth2 Scope — Space-delimited permissions granted',
      roles: 'User Roles — Array of RBAC roles assigned to subject',
      permissions: 'Permissions — Explicit authorities granted to subject',
      name: 'Full Name — Display name of the subject',
      email: 'Email Address — User contact email address'
    };

    let html = '';
    keys.forEach(k => {
      const val = payload[k];
      const desc = RFC_CLAIM_DESCRIPTIONS[k] || 'Custom application claim';
      const formattedVal = typeof val === 'object' ? JSON.stringify(val) : String(val);

      html += `
        <tr>
          <td><code>${escapeHtml(k)}</code></td>
          <td style="color:var(--text);">${escapeHtml(desc)}</td>
          <td><code style="color:#c084fc;">${escapeHtml(formattedVal)}</code></td>
        </tr>
      `;
    });

    tbody.innerHTML = html;
  }

  // --- POLYGLOT CODE GENERATOR ---
  function updateCodeSnippets() {
    const pre = document.getElementById('jwtCodePre');
    if (!pre) return;

    const token = state.token || 'YOUR_JWT_TOKEN_HERE';
    const secret = state.secret || 'your-256-bit-secret';
    const alg = state.headerObj.alg || 'HS256';
    const lang = state.activeCodeTab;

    let snippet = '';

    switch (lang) {
      case 'js':
        snippet = `// Node.js (npm install jsonwebtoken)
const jwt = require('jsonwebtoken');

const token = "${token}";
const secret = "${secret}";

try {
  // Verify and decode token:
  const decoded = jwt.verify(token, secret, { algorithms: ['${alg}'] });
  console.log("Token Verified Successfully!");
  console.log("Payload:", decoded);
} catch (err) {
  console.error("JWT Verification Failed:", err.message);
}

// Or generate a new signed token:
const newToken = jwt.sign(
  ${JSON.stringify(state.payloadObj, null, 2)},
  secret,
  { algorithm: '${alg}' }
);
console.log("New Token:", newToken);`;
        break;

      case 'python':
        snippet = `# Python 3 (pip install pyjwt)
import jwt

token = "${token}"
secret = "${secret}"

try:
    decoded = jwt.decode(
        token,
        secret,
        algorithms=["${alg}"]
    )
    print("Token is Valid! Decoded payload:")
    print(decoded)
except jwt.ExpiredSignatureError:
    print("Error: Signature has expired.")
except jwt.InvalidTokenError as e:
    print("Error: Invalid token -", e)`;
        break;

      case 'go':
        snippet = `// Golang (go get -u github.com/golang-jwt/jwt/v5)
package main

import (
\t"fmt"
\t"github.com/golang-jwt/jwt/v5"
)

func main() {
\ttokenString := "${token}"
\thmacSecret := []byte("${secret}")

\ttoken, err := jwt.Parse(tokenString, func(t *jwt.Token) (interface{}, error) {
\t\tif _, ok := t.Method.(*jwt.SigningMethodHMAC); !ok {
\t\t\treturn nil, fmt.Errorf("unexpected signing method: %v", t.Header["alg"])
\t\t}
\t\treturn hmacSecret, nil
\t})

\tif claims, ok := token.Claims.(jwt.MapClaims); ok && token.Valid {
\t\tfmt.Println("Token is valid! Subject:", claims["sub"])
\t} else {
\t\tfmt.Println("Invalid token:", err)
\t}
}`;
        break;

      case 'java':
        snippet = `// Java (io.jsonwebtoken:jjwt-api:0.12.5)
import io.jsonwebtoken.Jwts;
import io.jsonwebtoken.Claims;
import io.jsonwebtoken.security.Keys;
import java.nio.charset.StandardCharsets;

public class JwtVerifier {
    public static void main(String[] args) {
        String token = "${token}";
        String secret = "${secret}";

        try {
            var key = Keys.hmacShaKeyFor(secret.getBytes(StandardCharsets.UTF_8));
            Claims claims = Jwts.parser()
                    .verifyWith(key)
                    .build()
                    .parseSignedClaims(token)
                    .getPayload();

            System.out.println("Valid Token! Subject: " + claims.getSubject());
        } catch (Exception e) {
            System.err.println("JWT Verification Failed: " + e.getMessage());
        }
    }
}`;
        break;

      case 'csharp':
        snippet = `// C# (.NET 8 - System.IdentityModel.Tokens.Jwt)
using System;
using System.Text;
using System.IdentityModel.Tokens.Jwt;
using Microsoft.IdentityModel.Tokens;

class Program {
    static void Main() {
        string token = "${token}";
        string secret = "${secret}";

        var tokenHandler = new JwtSecurityTokenHandler();
        var key = Encoding.UTF8.GetBytes(secret);

        try {
            tokenHandler.ValidateToken(token, new TokenValidationParameters {
                ValidateIssuerSigningKey = true,
                IssuerSigningKey = new SymmetricSecurityKey(key),
                ValidateIssuer = false,
                ValidateAudience = false,
                ClockSkew = TimeSpan.Zero
            }, out SecurityToken validatedToken);

            Console.WriteLine("JWT Token Signature is VALID!");
        } catch (Exception ex) {
            Console.WriteLine($"Token Validation Failed: {ex.Message}");
        }
    }
}`;
        break;

      case 'php':
        snippet = `<?php
// PHP (composer require firebase/php-jwt)
require 'vendor/autoload.php';
use Firebase\\JWT\\JWT;
use Firebase\\JWT\\Key;

$token = "${token}";
$secret = "${secret}";

try {
    $decoded = JWT::decode($token, new Key($secret, '${alg}'));
    echo "JWT is Valid!\\n";
    print_r($decoded);
} catch (Exception $e) {
    echo "JWT Verification Failed: " . $e->getMessage() . "\\n";
}`;
        break;
    }

    pre.textContent = snippet;
  }

  // --- LOAD PRESET ---
  function loadPreset(key) {
    const p = PRESETS[key];
    if (!p) return;

    state.headerObj = JSON.parse(JSON.stringify(p.header));
    state.payloadObj = JSON.parse(JSON.stringify(p.payload));
    state.secret = p.secret;
    state.isSecretB64 = !!p.isSecretB64;

    const headerEditor = document.getElementById('jwtHeaderEditor');
    const payloadEditor = document.getElementById('jwtPayloadEditor');
    const secretInput = document.getElementById('jwtSecretInput');
    const secretB64Chk = document.getElementById('jwtSecretB64');

    if (headerEditor) headerEditor.value = JSON.stringify(state.headerObj, null, 2);
    if (payloadEditor) payloadEditor.value = JSON.stringify(state.payloadObj, null, 2);
    if (secretInput) secretInput.value = state.secret;
    if (secretB64Chk) secretB64Chk.checked = state.isSecretB64;

    encodeFromDecoded();
  }

  // --- CLIPBOARD HELPER ---
  function copyText(str, btn, successMsg = 'Copied!') {
    navigator.clipboard.writeText(str).then(() => {
      const orig = btn.innerHTML;
      btn.innerHTML = `<span>✓</span> ${successMsg}`;
      btn.classList.add('success');
      setTimeout(() => {
        btn.innerHTML = orig;
        btn.classList.remove('success');
      }, 1600);
    }).catch(() => {
      alert('Could not copy to clipboard.');
    });
  }

  // --- ESCAPE HTML ---
  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // --- INITIALIZATION ---
  document.addEventListener('DOMContentLoaded', () => {
    const tokenTextarea = document.getElementById('jwtTokenTextarea');
    const headerEditor = document.getElementById('jwtHeaderEditor');
    const payloadEditor = document.getElementById('jwtPayloadEditor');
    const secretInput = document.getElementById('jwtSecretInput');
    const secretB64Chk = document.getElementById('jwtSecretB64');

    // 1. Listen on Raw Token Textarea input
    if (tokenTextarea) {
      let debounce = null;
      tokenTextarea.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          decodeFromToken(tokenTextarea.value);
        }, 150);
      });
    }

    // 2. Listen on Header Editor input
    if (headerEditor) {
      let debounce = null;
      headerEditor.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          try {
            state.headerObj = JSON.parse(headerEditor.value);
            encodeFromDecoded();
          } catch (e) {
            // Wait until valid JSON
          }
        }, 200);
      });
    }

    // 3. Listen on Payload Editor input
    if (payloadEditor) {
      let debounce = null;
      payloadEditor.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          try {
            state.payloadObj = JSON.parse(payloadEditor.value);
            encodeFromDecoded();
          } catch (e) {
            // Wait until valid JSON
          }
        }, 200);
      });
    }

    // 4. Listen on Secret Input & B64 checkbox
    if (secretInput) {
      secretInput.addEventListener('input', () => {
        state.secret = secretInput.value;
        encodeFromDecoded();
      });
    }

    if (secretB64Chk) {
      secretB64Chk.addEventListener('change', () => {
        state.isSecretB64 = secretB64Chk.checked;
        encodeFromDecoded();
      });
    }

    // 5. Presets
    document.querySelectorAll('.jwt-preset-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const key = chip.getAttribute('data-preset');
        loadPreset(key);
        window.Analytics?.track('preset_select', { tool_name: 'jwt_inspector', preset_name: key });
      });
    });

    // 6. Action buttons
    const btnCopyToken = document.getElementById('jwtBtnCopyToken');
    if (btnCopyToken) {
      btnCopyToken.addEventListener('click', () => {
        copyText(state.token, btnCopyToken, 'Token Copied!');
      });
    }

    const btnClearToken = document.getElementById('jwtBtnClearToken');
    if (btnClearToken) {
      btnClearToken.addEventListener('click', () => {
        state.token = '';
        if (tokenTextarea) tokenTextarea.value = '';
        renderInvalidToken('Empty token');
        updateStatusBadges();
        window.Analytics?.trackToolAction('jwt_inspector', 'clear_token');
      });
    }

    const btnShare = document.getElementById('jwtBtnShare');
    if (btnShare) {
      btnShare.addEventListener('click', () => {
        const params = new URLSearchParams();
        params.set('token', state.token);
        if (state.secret) params.set('secret', state.secret);
        window.location.hash = params.toString();
        copyText(window.location.href, btnShare, 'Share URL Copied!');
        window.Analytics?.track('share_link', { tool_name: 'jwt_inspector' });
      });
    }

    const btnCopyHeader = document.getElementById('jwtBtnCopyHeader');
    if (btnCopyHeader && headerEditor) {
      btnCopyHeader.addEventListener('click', () => {
        copyText(headerEditor.value, btnCopyHeader, 'Header Copied!');
      });
    }

    const btnCopyPayload = document.getElementById('jwtBtnCopyPayload');
    if (btnCopyPayload && payloadEditor) {
      btnCopyPayload.addEventListener('click', () => {
        copyText(payloadEditor.value, btnCopyPayload, 'Payload Copied!');
      });
    }

    // 7. Code Snippet Tabs
    document.querySelectorAll('.rv-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        document.querySelectorAll('.rv-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        state.activeCodeTab = tab.getAttribute('data-lang');
        updateCodeSnippets();
        window.Analytics?.trackToolAction('jwt_inspector', 'switch_code_tab', { language: state.activeCodeTab });
      });
    });

    const btnCopyCode = document.getElementById('jwtBtnCopyCode');
    const codePre = document.getElementById('jwtCodePre');
    if (btnCopyCode && codePre) {
      btnCopyCode.addEventListener('click', () => {
        copyText(codePre.textContent, btnCopyCode, 'Code Copied!');
      });
    }

    // 8. Restore from URL Hash or initial load
    let loadedFromHash = false;
    if (window.location.hash && window.location.hash.length > 1) {
      try {
        const params = new URLSearchParams(window.location.hash.slice(1));
        if (params.has('token')) {
          const t = params.get('token');
          if (params.has('secret')) state.secret = params.get('secret');
          if (secretInput) secretInput.value = state.secret;
          decodeFromToken(t);
          loadedFromHash = true;
        }
      } catch (e) {}
    }

    if (!loadedFromHash) {
      loadPreset('session');
    }
  });

})();
